import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useHomeEditor } from "@/app/features/sites/home/contexts/HomeContext";
import Grid from "@mui/material/Grid2";
import MediaView from "@/app/features/shared/media-view/ui/public/MediaView.public";
import { HomeTileEditable } from "@/app/features/sites/home/types/form-data/SiteHome.schema";
import { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import { mediaRefUrl } from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import sharedStyles from "@/app/features/sites/home/ui/shared/home-shared.module.css";
import { HomeTileCardFrame, HomeTilesFrame } from "@/app/features/sites/home/ui/shared/HomeViewFrames";
import EditableTypographyV2 from "@/app/features/shared/editable-typography/EditableTypographyV2";
import styles from "@/app/features/sites/home/ui/editable/home-editable.module.css";
import clsx from "clsx";

export default function HomeTiles() {
    const { editable, updateEditable } = useHomeEditor();
    const tilesRef = useRef(editable?.tiles ?? []);

    const tiles = useMemo(() =>
            [...editable?.tiles || []]
                .sort((a, b) => a.order - b.order),
        [editable?.tiles]
    );

    useEffect(() => {
        tilesRef.current = editable?.tiles ?? [];
    }, [editable?.tiles]);

    const updateTile = useCallback((partialTile: Omit<Partial<HomeTileEditable>, 'id'> & { id: string }) => {
        updateEditable({
            tiles: tilesRef.current.map((tile) =>
                tile.id === partialTile.id ? { ...tile, ...partialTile } : tile
            )
        });
    }, [updateEditable]);

    return (
        <HomeTilesFrame>
            {tiles.map((tile) => (
                <Grid key={tile.id} size={1}>
                    <TileItem
                        tile={tile}
                        onUpdate={updateTile}
                    />
                </Grid>
            ))}
        </HomeTilesFrame>
    );
}

const TileItem = memo(({
    tile,
    onUpdate,
}: {
    tile: HomeTileEditable;
    onUpdate: (partialTile: Omit<Partial<HomeTileEditable>, 'id'> & { id: string }) => void;
}) => {
    const updateLabel = useCallback((label: string) => {
        onUpdate({
            id: tile.id,
            label
        });
    }, [tile.id, onUpdate]);

    return (
        <HomeTileCardFrame animated={false}>
            <EditableTypographyV2
                variant="h2"
                value={tile.label}
                onChange={updateLabel}
                slots={tileLabelSlots}
            />
            <TileMedia src={tile.image} />
        </HomeTileCardFrame>
    );
}, (prev, next) => prev.tile === next.tile);
TileItem.displayName = "TileItem";

const tileLabelSlots = {
    positioner: { className: styles.homeTileEditableTitlePositioner },
    root: {
        className: clsx(
            'signature-border-radius glass-effect',
            styles.homeTileEditableTitleBox,
        ),
    },
};

const TileMedia = memo(
    function TileMedia({
        src,
    }: {
        src: MediaRef;
    }) {
        return (
            <MediaView
                src={src}
                fill
                showLoadingSpinner={false}
                slots={tileMediaSlots}
            />
        );
    },
    (prev, next) => mediaRefUrl(prev.src) === mediaRefUrl(next.src)
);

const tileMediaSlots = {
    root: {
        className: "size-full",
        style: {
            zIndex: 10,
        },
    },
    image: {
        className: `${sharedStyles.tileImage} object-cover`,
        style: {
            height: '100%',
            width: '100%',
            cursor: 'default',
        },
    },
};
